// /web/src/pages/calculators/Stairs.jsx
import React from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useApi } from "../../lib/api";
import { trackCalculatorRun } from "../../lib/amplitude";
import { toast } from "react-hot-toast";
import useUsageEvent from "../../hooks/useUsageEvent";
import CalculatorLayout, {
  CalculatorSection,
} from "../../components/calculators/CalculatorLayout.jsx";
import DownloadStairReportBtn from "../../components/pdf/DownloadStairReportBtn.jsx";

function toErrorMessage(err) {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (err.detail) {
    if (typeof err.detail === "string") return err.detail;
    if (Array.isArray(err.detail) && err.detail[0]?.msg) return err.detail[0].msg;
  }
  if (typeof err.message === "string" && err.message !== "[object Object]") return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

const STAIR_CALCULATOR_META = {
  title: "Staircase Design Calculator",
  notes: [
    "We assume a minimum clear stair width of 1100 mm and convert level heights into risers per BS 5395 guidance.",
    "Total building height determines whether 1 or 2 escape stairs are required (threshold at 18 m).",
    "Refresh from the project to keep structural levels aligned with the geometry saved in Project Inputs.",
  ],
  resultNotes: [
    "The controlling floor is whichever level produced the largest riser count or riser height.",
    "Increase core width if additional plant or smoke ventilation zones are needed within the shaft.",
  ],
};

function formatMetres(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)} m`;
}

function formatMillimetres(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n)} mm`;
}

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toString();
}

export default function StairsCalculator() {
  const api = useApi();
  const params = useParams();
  const [qs] = useSearchParams();
  const projectId =
    params.id ||
    qs.get("project_id") ||
    qs.get("projectId") ||
    qs.get("project") ||
    null;
  const rawUsageEventId = qs.get("usage_event_id");
  const { event: historyEvent, loading: historyLoading, error: historyError, usageEventId } =
    useUsageEvent(rawUsageEventId);

  const [project, setProject] = React.useState(null);
  const [projects, setProjects] = React.useState([]);
  const [loadingProjects, setLoadingProjects] = React.useState(!projectId);
  const [pickErr, setPickErr] = React.useState("");

  // Form state
  const [buildingHeight, setBuildingHeight] = React.useState("");
  const [floorHeights, setFloorHeights] = React.useState([]); // numbers in meters
  const [floorSectionOpen, setFloorSectionOpen] = React.useState(false);
  const [entranceIndex, setEntranceIndex] = React.useState(null);

  // UX state
  const [loading, setLoading] = React.useState(false);
  const [prefillLoading, setPrefillLoading] = React.useState(!!projectId);
  const [error, setError] = React.useState("");

  // Result state
  const [result, setResult] = React.useState(null);

  const applyProjectData = React.useCallback((proj, rows) => {
    setProject(proj || null);

    let entryIdx = null;
    if (Array.isArray(rows) && rows.length) {
      for (const r of rows) {
        if (r.is_entrance_floor) {
          entryIdx = r.floor_index;
          break;
        }
      }
    }
    setEntranceIndex(entryIdx);

    const heights = Array.isArray(rows)
      ? rows
          .map((r) => (r.floor_to_floor_height_m != null ? Number(r.floor_to_floor_height_m) : null))
          .map((n) => (Number.isFinite(n) && n > 0 ? n : null))
          .filter((n) => n != null)
      : [];

    if (heights.length > 0) {
      setFloorHeights(heights);
      const sum = heights.reduce((a, b) => a + b, 0);
      setBuildingHeight(String(Number(sum.toFixed(2))));
    } else {
      setFloorHeights([]);
      setBuildingHeight("");
    }
  }, []);

  // ---------- Prefill from project on load or show picker ----------
  React.useEffect(() => {
    let active = true;

    async function loadProjectAndInputs(pid) {
      try {
        setPrefillLoading(true);
        setError("");
        setResult(null);
        const [proj, rows] = await Promise.all([
          api(`/projects/${pid}`),
          api(`/projects/${pid}/inputs`),
        ]);

        if (!active) return;
        applyProjectData(proj, rows);
      } catch (e) {
        if (!active) return;
        setProject(null);
        setEntranceIndex(null);
        setFloorHeights([]);
        setBuildingHeight("");
        setResult(null);
        setError(toErrorMessage(e) || "Failed to load project floors");
      } finally {
        if (active) setPrefillLoading(false);
      }
    }

    async function loadProjectsList() {
      try {
        setLoadingProjects(true);
        setPickErr("");
        const list = await api(`/projects`);
        if (!active) return;
        setProjects(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!active) return;
        setPickErr(toErrorMessage(e) || "Failed to load projects");
      } finally {
        if (active) setLoadingProjects(false);
      }
    }

    if (projectId) {
      setLoadingProjects(false);
      setPickErr("");
      setProjects([]);
      loadProjectAndInputs(projectId);
    } else if (!usageEventId) {
      setProject(null);
      setEntranceIndex(null);
      setFloorHeights([]);
      setBuildingHeight("");
      setResult(null);
      setError("");
      setPrefillLoading(false);
      loadProjectsList();
    } else {
      setProject(null);
      setEntranceIndex(null);
      setFloorHeights([]);
      setBuildingHeight("");
      setResult(null);
      setError("");
      setPrefillLoading(false);
      setLoadingProjects(false);
      setProjects([]);
      setPickErr("");
    }

    return () => {
      active = false;
    };
  }, [projectId, usageEventId, api, applyProjectData]);

  React.useEffect(() => {
    if (!usageEventId || !historyEvent) return;
    const details = historyEvent.details || {};
    if (typeof details.building_height_m === "number") {
      setBuildingHeight(String(details.building_height_m));
    }
    const heights = Array.isArray(details.floor_heights_m)
      ? details.floor_heights_m
          .map((n) => Number(n))
          .map((n) => (Number.isFinite(n) && n > 0 ? Number(n.toFixed(3)) : null))
          .filter((n) => n != null)
      : [];
    if (heights.length > 0) {
      setFloorHeights(heights);
      const sum = heights.reduce((a, b) => a + b, 0);
      setBuildingHeight((prev) => {
        const prevNum = Number(prev);
        if (Number.isFinite(prevNum) && prevNum > 0) return prev;
        return String(Number(sum.toFixed(2)));
      });
      setFloorSectionOpen(true);
    }
    if (historyEvent.result_json) {
      setResult(historyEvent.result_json);
    }
  }, [usageEventId, historyEvent]);

  // ---------- Floor labels from entrance floor ----------
  function numberToOrdinal(n) {
    const map = [
      "", "First", "Second", "Third", "Fourth", "Fifth",
      "Sixth", "Seventh", "Eighth", "Ninth", "Tenth",
      "Eleventh", "Twelfth", "Thirteenth", "Fourteenth", "Fifteenth",
    ];
    if (n < map.length) return map[n];
    return `${n}th`;
  }
  function floorLabel(idx) {
    if (typeof idx !== "number") return "Level";
    if (entranceIndex === null || entranceIndex === undefined) return `Level ${idx}`;
    const rel = idx - entranceIndex;
    if (rel === 0) return "Ground Floor";
    if (rel > 0) return `${numberToOrdinal(rel)} Floor`;
    if (rel === -1) return "Basement";
    return `Basement ${Math.abs(rel)}`;
  }

  // ---------- Handlers ----------
  function addFloor() {
    setFloorHeights((h) => [...h, 3.5]);
    setFloorSectionOpen(true);
  }
  function removeFloor(index) { setFloorHeights((h) => h.filter((_, i) => i !== index)); }
  function updateFloor(index, value) {
    const v = value === "" ? "" : Number(value);
    setFloorHeights((h) => h.map((x, i) => (i === index ? v : x)));
  }
  function onChangeBuildingHeight(e) { setBuildingHeight(e.target.value); }

  async function refreshFromProject() {
    if (!projectId) return;
    setPrefillLoading(true);
    setError("");
    try {
      const [proj, rows] = await Promise.all([
        api(`/projects/${projectId}`),
        api(`/projects/${projectId}/inputs`),
      ]);
      if (!Array.isArray(rows) || !rows.length) throw new Error("No saved floors in this project yet.");
      applyProjectData(proj, rows);
      setError("");
    } catch (e) {
      setError(toErrorMessage(e) || "Refresh failed");
    } finally {
      setPrefillLoading(false);
    }
  }

  async function runCalc(e) {
    e?.preventDefault?.();
    setLoading(true);
    setResult(null);
    setError("");
    try {
      const bh = Number(buildingHeight);
      const fh = floorHeights.map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0);
      if (!Number.isFinite(bh) || bh <= 0) throw new Error("Enter a valid total building height (m).");
      if (!fh.length) throw new Error("Add at least one valid floor height (m).");

      const body = { building_height_m: bh, floor_heights_m: fh };
      const path = projectId ? `/calc/stairs/run?project_id=${projectId}` : `/calc/stairs/run`;
      const res = await api(path, { method: "POST", body });
      const staircases = Number(res?.number_of_staircases);
      const coreWidth = Number(res?.stair_core_width_m);
      const shaftLength = Number(res?.required_shaft_length_m);
      trackCalculatorRun("stairs", {
        projectId,
        inputs: body,
        resultSummary: {
          staircases: Number.isFinite(staircases) ? staircases : undefined,
          controlling_floor_index:
            typeof res?.controlling_floor_index === "number" ? res.controlling_floor_index : undefined,
          controlling_configuration: res?.controlling_floor_config?.configuration,
          core_width_m: Number.isFinite(coreWidth) ? coreWidth : undefined,
          shaft_length_m: Number.isFinite(shaftLength) ? shaftLength : undefined,
        },
      });
      setResult(res);
      toast.success("Calculation complete");
    } catch (e) {
      const message = toErrorMessage(e) || "Calculation failed";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }

  const totalHeights = floorHeights
    .map((n) => (Number.isFinite(n) ? n : 0))
    .reduce((a, b) => a + b, 0);

  const showPicker = !projectId && !usageEventId;
  const projectSummary = project
    ? [
        project.name,
        project.postcode,
        project.local_authority,
        project.floors_count ? `${project.floors_count} floors` : null,
      ].filter(Boolean)
    : null;
  const hasResults = !!result;
  const floors = Array.isArray(result?.floors) ? result.floors : [];
  const controllingFloorLabel =
    typeof result?.controlling_floor_index === "number"
      ? floorLabel(result.controlling_floor_index)
      : "Controlling floor";

  const historyProject = historyEvent?.project_id
    ? { id: historyEvent.project_id, name: historyEvent.project_name }
    : null;
  const layoutProject = project || historyProject;
  const historySummary = historyEvent
    ? [
        historyEvent.project_name || "Saved run",
        historyEvent.created_at
          ? new Date(historyEvent.created_at).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : null,
      ].filter(Boolean)
    : null;
  const layoutDetails = project ? projectSummary : historySummary;
  const historyBanner = (() => {
    if (!usageEventId) return null;
    if (historyLoading) return "Loading saved calculator run…";
    if (historyError) return `Failed to load saved run: ${historyError}`;
    if (historyEvent) {
      const ts = historyEvent.created_at
        ? new Date(historyEvent.created_at).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          })
        : null;
      return ts
        ? `Restored calculator inputs from ${ts}. Update any values and rerun to refresh.`
        : "Restored calculator inputs from your history.";
    }
    return null;
  })();

  return (
    <CalculatorLayout
      title={STAIR_CALCULATOR_META.title}
      project={layoutProject}
      projectDetails={layoutDetails}
      onRun={showPicker ? null : runCalc}
      runLabel="Run calculation"
      runDisabled={showPicker || loading || prefillLoading}
      runLoading={loading}
    >
      {usageEventId && historyBanner && (
        <div className="pd-inline-alert pd-inline-alert--info">{historyBanner}</div>
      )}

      {showPicker ? (
        <CalculatorSection
          title="Select a project"
          description="Pick a project to run the staircase provision calculator."
        >
          {pickErr && (
            <div className="pd-alert">
              <strong>Error:</strong> {pickErr}
            </div>
          )}
          {loadingProjects ? (
            <div className="pd-results-placeholder pd-results-placeholder--auto">
              Loading your projects…
            </div>
          ) : projects.length === 0 ? (
            <div className="pd-empty-state">
              <p>You don’t have any projects yet.</p>
              <div className="pd-empty-state__actions">
                <Link to="/projects/new" className="pd-btn pd-btn--primary">
                  + Create new project
                </Link>
              </div>
            </div>
          ) : (
            <div className="pd-project-grid">
              {projects.map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}/calculators/stairs`}
                  className="pd-project-card"
                >
                  <div className="pd-project-card__head">
                    <div>
                      <div className="pd-project-card__title">{p.name}</div>
                      <div className="pd-project-card__meta">
                        {p.postcode || "—"} · {p.local_authority || "—"}
                      </div>
                    </div>
                    <span className="pd-pill">{(p.building_use || "office").toUpperCase()}</span>
                  </div>
                  <div className="pd-project-card__foot">
                    Floors: {p.floors_count ?? "—"}
                    <span className="pd-project-card__cta">Open staircase calculator →</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CalculatorSection>
      ) : (
        <>
          {prefillLoading && <div className="pd-caption">Syncing project floors…</div>}
          {error && (
            <div className="pd-alert">
              <strong>Error:</strong> {error}
            </div>
          )}

          <CalculatorSection
            title="Input parameters"
            description="Provide the total building height and level-by-level floor-to-floor heights."
          >
            <div className="pd-input-grid">
              <div className="pd-form-group">
                <label htmlFor="stairs-building-height">Total building height (m)</label>
                <input
                  id="stairs-building-height"
                  type="number"
                  step="0.01"
                  value={buildingHeight}
                  onChange={onChangeBuildingHeight}
                  placeholder="e.g. 22.8"
                />
                <p className="pd-form-note">
                  Used for stair count rule (below 18 m → 1 stair, 18 m and above → 2 stairs).
                </p>
              </div>

              <div className="pd-form-group">
                <label htmlFor="stairs-floor-count">Captured levels</label>
                <input
                  id="stairs-floor-count"
                  type="text"
                  value={`${floorHeights.length} level${floorHeights.length === 1 ? "" : "s"}`}
                  readOnly
                />
                <p className="pd-form-note">Sum: {totalHeights.toFixed(2)} m</p>
              </div>
            </div>

              <div className="pd-panel">
                <div className="pd-panel__head">
                  <div>
                    <div className="pd-panel__title">Floor-to-floor heights (m)</div>
                    <div className="pd-panel__meta">
                      {floorHeights.length} level{floorHeights.length === 1 ? "" : "s"} captured • Sum {totalHeights.toFixed(2)} m
                    </div>
                  </div>
                  <div className="pd-panel__actions">
                    <button
                      type="button"
                      className="pd-btn pd-btn--ghost pd-btn--compact"
                      onClick={addFloor}
                    >
                      Add level
                    </button>
                    {projectId && (
                      <button
                        type="button"
                        className="pd-btn pd-btn--ghost pd-btn--compact"
                        onClick={refreshFromProject}
                        disabled={prefillLoading}
                      >
                        {prefillLoading ? "Refreshing…" : "Refresh from project floors"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="pd-btn pd-btn--primary pd-btn--compact"
                      onClick={() => setFloorSectionOpen((open) => !open)}
                      aria-expanded={floorSectionOpen}
                    >
                      {floorSectionOpen ? "Hide levels" : "Show levels"}
                    </button>
                  </div>
                </div>

              {floorSectionOpen ? (
                <>
                  <div className="pd-floor-list">
                    {floorHeights.length === 0 ? (
                      <div className="pd-inline-placeholder">No levels added yet.</div>
                    ) : (
                      floorHeights.map((h, i) => {
                        const value = h === "" ? "" : String(h);
                        const summary = Number.isFinite(h) ? `${Number(h).toFixed(2)} m` : "Enter a height";
                        return (
                          <div key={i} className="pd-floor-row">
                            <div className="pd-floor-row__label">
                              <div className="pd-floor-row__name">{floorLabel(i)}</div>
                              <div className="pd-floor-row__meta">{summary}</div>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              value={value}
                              onChange={(e) => updateFloor(i, e.target.value)}
                              placeholder="e.g. 3.6"
                            />
                            <button
                              type="button"
                              className="pd-btn pd-btn--ghost pd-btn--compact"
                              onClick={() => removeFloor(i)}
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="pd-panel__footer">
                    <span>Sum of floor heights</span>
                    <strong>{totalHeights.toFixed(2)} m</strong>
                  </div>
                </>
              ) : (
                <div className="pd-inline-placeholder">Expand to edit level heights.</div>
              )}
            </div>
          </CalculatorSection>

          <CalculatorSection title="Notes" description="Key considerations before issuing results.">
            <div className="pd-calculator__notes">
              <ul className="pd-notes-list">
                {STAIR_CALCULATOR_META.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </div>
          </CalculatorSection>

          <CalculatorSection
            title="Results"
            description="Run the calculator to size stair cores and shafts."
            actions={
              <DownloadStairReportBtn
                project={layoutProject}
                result={result}
                entranceIdx={entranceIndex}
                disabled={loading || prefillLoading || historyLoading || !hasResults}
              />
            }
          >
            {!hasResults ? (
              <div className="pd-results-placeholder">
                Run the calculator to see required shaft size, summary, and per-floor breakdown.
              </div>
            ) : (
              <>
                <div className="pd-summary-card">
                  <div className="pd-summary-card__head">
                    <div>
                      <div className="pd-summary-card__title">Required shaft size</div>
                      <div className="pd-summary-card__meta">
                        Dictated by {controllingFloorLabel} ({
                          result?.controlling_floor_config?.configuration || "—"
                        })
                      </div>
                    </div>
                  </div>
                  <div className="pd-summary-metric">
                    {formatMetres(result?.stair_core_width_m)} × {formatMetres(result?.required_shaft_length_m)}
                  </div>
                  <div className="pd-summary-grid">
                    <div className="pd-summary-tile">
                      <div className="pd-summary-tile__title">Staircases required</div>
                      <div className="pd-summary-tile__stat">{formatCount(result?.number_of_staircases)}</div>
                    </div>
                    <div className="pd-summary-tile">
                      <div className="pd-summary-tile__title">Core width</div>
                      <div className="pd-summary-tile__stat">{formatMetres(result?.stair_core_width_m)}</div>
                    </div>
                    <div className="pd-summary-tile">
                      <div className="pd-summary-tile__title">Core length</div>
                      <div className="pd-summary-tile__stat">{formatMetres(result?.required_shaft_length_m)}</div>
                    </div>
                    <div className="pd-summary-tile">
                      <div className="pd-summary-tile__title">Risers / flight (controlling)</div>
                      <div className="pd-summary-tile__stat">
                        {formatCount(result?.controlling_floor_config?.risers_per_flight)}
                      </div>
                    </div>
                    <div className="pd-summary-tile">
                      <div className="pd-summary-tile__title">Riser height (controlling)</div>
                      <div className="pd-summary-tile__stat">
                        {formatMillimetres(result?.controlling_floor_config?.actual_riser_height_mm)}
                      </div>
                    </div>
                  </div>
                </div>

                <section className="pd-results-card">
                  <h3 className="pd-h4">Floor-by-floor breakdown</h3>
                  <div className="pd-results-card__meta">{floors.length} level{floors.length === 1 ? "" : "s"}</div>
                  <div className="pd-table-wrapper">
                    <table className="pd-table">
                      <thead>
                        <tr>
                          <th scope="col">Floor</th>
                          <th scope="col">Height (m)</th>
                          <th scope="col">Configuration</th>
                          <th scope="col">Core length (m)</th>
                          <th scope="col">Total risers</th>
                          <th scope="col">Risers / flight</th>
                          <th scope="col">Riser height (mm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {floors.map((f) => (
                          <tr key={f.floor_index}>
                            <td>{floorLabel(f.floor_index)}</td>
                            <td>{formatMetres(f.floor_height_m)}</td>
                            <td>{f.configuration || "—"}</td>
                            <td>{formatMetres(f.stair_core_length_m)}</td>
                            <td>{formatCount(f.total_risers)}</td>
                            <td>{formatCount(f.risers_per_flight)}</td>
                            <td>{formatMillimetres(f.actual_riser_height_mm)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {STAIR_CALCULATOR_META.resultNotes.length > 0 && (
                  <div className="pd-summary-note">
                    <p className="pd-summary-note__meta">Result notes</p>
                    <ul className="pd-notes-list">
                      {STAIR_CALCULATOR_META.resultNotes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </CalculatorSection>
        </>
      )}
    </CalculatorLayout>
  );
}
